import {
  MathUtils,
  OrthographicCamera,
  PerspectiveCamera,
  Vector3,
} from "three";
import { CameraViewType, type CameraView } from "@/utils/cam-view";
import {
  clashCandidateBounds,
  type ClashCandidate,
} from "./cad-clash-detection";
const MIN_RADIUS = 0.15;
const DEFAULT_FOV = 50;
const FRAME_MARGIN = 1.5;

/**
 *
 * @param camera Current projection and orientation.
 * @param candidate Observed clash location and extent.
 * @param aspect Available viewport width divided by height.
 * @param boundsFrame The owning run's `boundsFrame`.
 * @returns An existing camera-view command with an explicit clash pivot.
 */
export function clashCameraView(
  camera: PerspectiveCamera | OrthographicCamera,
  candidate: ClashCandidate,
  aspect: number,
  boundsFrame?: number[],
): CameraView {
  const target = new Vector3().fromArray(candidate.position);
  const bounds = clashCandidateBounds(candidate, boundsFrame);
  if (candidate.witness) {
    bounds.expandByPoint(new Vector3().fromArray(candidate.witness.scan));
    bounds.expandByPoint(new Vector3().fromArray(candidate.witness.cad));
  }
  const radius = Math.max(
    MIN_RADIUS,
    target.distanceTo(bounds.min),
    target.distanceTo(bounds.max),
  );
  const ortho = camera instanceof OrthographicCamera;
  const fov = camera instanceof PerspectiveCamera ? camera.fov : DEFAULT_FOV;
  const angle = Math.atan(
    Math.tan(MathUtils.degToRad(fov / 2)) * Math.min(1, Math.max(0.1, aspect)),
  );
  const distance = Math.max(
    camera.near * 2 + radius,
    (radius * FRAME_MARGIN) / Math.sin(angle),
  );
  return {
    type: ortho ? CameraViewType.orthographic : CameraViewType.perspective,
    position: target
      .clone()
      .sub(camera.getWorldDirection(new Vector3()).multiplyScalar(distance)),
    rotation: camera.quaternion.clone(),
    target,
    zoomLevel: ortho ? (radius * 3) / Math.min(1, Math.max(0.1, aspect)) : fov,
  };
}
